import type { RequestState } from "../../services/api/requestState";
import type * as m from "../model/model";
import type { ErrorEnvelope, SuccessEnvelope } from "../model/types";

export const toEnvelope = <T>(data: T, error?: 0): SuccessEnvelope<T> => ({
	Success: true,
	Data: data,
	Error: error,
	ErrorMessage: undefined,
	ErrorCode: undefined,
});

export const toErrorEnvelope = <TError>(error: TError, message: string, code?: string): ErrorEnvelope<TError> => ({
	Success: false,
	Data: undefined,
	Error: error,
	ErrorMessage: message,
	ErrorCode: code,
});

export const fromEnvelope = <T, TError>(envelope: m.Envelope<T, TError>): T => {
	if (!envelope.Success) {
		throw new Error(envelope.ErrorMessage);
	}
	return envelope.Data;
};

export const withErrorMessageFromEnvelope = <T, Err>(rq: RequestState<m.Envelope<T, Err>>) => {
	if (rq.data && rq.data.Success === false && rq.data.ErrorMessage !== undefined) {
		return {
			isLoading: rq.isLoading,
			error: true,
			errorMessage: rq.data.ErrorMessage,
			data: rq.data,
			errorCode: rq.data.ErrorCode,
		};
	}
	return rq;
};

export const anyIsLoading = (...loaders: RequestState<unknown>[]) => loaders.some(state => state.isLoading);

export const anyIsLoadingOrHasNoData = (...loaders: RequestState<unknown>[]) =>
	loaders.some(state => state.isLoading || !state.data);

/**
 * Takes a function(arg1, arg2, ..) and converts it to function([arg1, arg2, ...])
 **/
export const createEndpoint = <Args extends unknown[], V>(fn: (...args: Args) => V) => {
	const newEndpoint = (args: Args) => fn(...args);
	return newEndpoint;
};

export const handleSuccessEnvelope = <T, TError>(
	envelope: m.Envelope<T, TError>,
	successFn: (data: T) => void,
	errorfn: (error: TError) => void,
) => {
	if (envelope.Success) {
		successFn(envelope.Data);
	} else {
		errorfn(envelope.Error);
	}
};
